import { $createParagraphNode, $getRoot, createEditor } from 'lexical';
import { $convertFromMarkdownString, $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { CodeHighlightNode, CodeNode } from '@lexical/code';

// Every node the editor can render. Anything outside this list cannot survive a round trip;
// unsupported constructs such as tables and images are kept as literal text instead.
export const MARKDOWN_NODES = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  LinkNode,
  CodeNode,
  CodeHighlightNode,
];

const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

// Guards the link tool. Without it a user can store `javascript:` in a post, which the
// public renderer would then have to strip. Relative urls resolve against a dummy base so
// they are allowed, since the reader is already on the site.
export function isSafeUrl(url) {
  const value = (url || '').trim();
  if (!value) return false;
  // Control characters can hide a scheme from a naive prefix check.
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return false;
  }
  try {
    return SAFE_PROTOCOLS.has(new URL(value, 'https://placeholder.invalid/').protocol);
  } catch {
    return false;
  }
}

// `true` on both sides keeps single newlines as real line breaks, matching how
// the public renderer treats the stored markdown (remark-breaks).
export function createMarkdownEditor() {
  return createEditor({
    namespace: 'MarkdownEditor',
    nodes: MARKDOWN_NODES,
    onError: (error) => {
      throw error;
    },
  });
}

export function $loadMarkdownInto(editor, markdown) {
  const root = $getRoot();
  root.clear();
  $convertFromMarkdownString(markdown || '', TRANSFORMERS, root, true);
  // An empty editor still needs one block or there is nowhere to put the caret.
  if (root.getChildrenSize() === 0) root.append($createParagraphNode());
}

export function markdownToEditorState(markdown) {
  const editor = createMarkdownEditor();
  editor.update(() => $loadMarkdownInto(editor, markdown), { discrete: true });
  return editor;
}

export function editorStateToMarkdown(editor) {
  return editor
    .getEditorState()
    .read(() => $convertToMarkdownString(TRANSFORMERS, undefined, true).trim());
}

export function markdownRoundTrip(markdown) {
  return editorStateToMarkdown(markdownToEditorState(markdown));
}
